import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

import DietTrackerIcon from '../assets/Diet Tracker Default.png';
import LineGraphIcon from '../assets/Line Graph Default.png';
import WorkoutHistoryIcon from '../assets/Workout History Default.png';
import BlogDefaultIcon from '../assets/Blog Default.png';
import BullseyeIcon from '../assets/Bullseye Default.png';

const formatDate = (date) => date.toISOString().split('T')[0];
const formatDisplayDate = (date) => date.toLocaleDateString('en-US', { 
  weekday: 'long', 
  year: 'numeric', 
  month: 'long', 
  day: 'numeric' 
});



export default function DietPlanner({ user }) {
  const [goals, setGoals] = useState({
    daily_calories: '',
    daily_protein: '',
    daily_carbs: '',
    daily_fats: ''
  });
  
  const [dailyEntry, setDailyEntry] = useState({
    calories: '',
    protein: '',
    carbs: '',
    fats: '',
    notes: ''
  });
  
  const [nutritionEntries, setNutritionEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingEntry, setEditingEntry] = useState(null);
  const [editingGoals, setEditingGoals] = useState(false);

  // Fetch nutrition goals
  const fetchGoals = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('nutrition_goals')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
        console.error('Error fetching goals:', error);
        setError('Failed to load nutrition goals');
      } else if (data) {
        setGoals({
          daily_calories: data.daily_calories || '',
          daily_protein: data.daily_protein || '',
          daily_carbs: data.daily_carbs || '',
          daily_fats: data.daily_fats || ''
        });
      } else {
        // No goals exist yet, show the form
        setEditingGoals(true);
      }
    } catch (err) {
      console.error('Goals fetch error:', err);
      setError('Failed to load nutrition goals');
    }
  }, [user.id]);

  // Fetch nutrition entries (last 30 days)
  const fetchEntries = useCallback(async () => {
    try {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const { data, error } = await supabase
        .from('nutrition_entries')
        .select('*')
        .eq('user_id', user.id)
        .gte('entry_date', formatDate(thirtyDaysAgo))
        .order('entry_date', { ascending: false });

      if (error) {
        console.error('Error fetching entries:', error);
        setError('Failed to load nutrition entries');
      } else if (data) {
        setNutritionEntries(data);
      }
    } catch (err) {
      console.error('Entries fetch error:', err);
      setError('Failed to load nutrition entries');
    }
  }, [user.id]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchGoals(), fetchEntries()]);
      setLoading(false);
    };
    
    if (user?.id) {
      loadData();
    }
  }, [user?.id, fetchGoals, fetchEntries]);

  // Save nutrition goals
  const handleGoalsSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const goalsData = {
        user_id: user.id,
        daily_calories: goals.daily_calories ? parseInt(goals.daily_calories, 10) : null,
        daily_protein: goals.daily_protein ? parseInt(goals.daily_protein, 10) : null,
        daily_carbs: goals.daily_carbs ? parseInt(goals.daily_carbs, 10) : null,
        daily_fats: goals.daily_fats ? parseInt(goals.daily_fats, 10) : null,
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('nutrition_goals')
        .upsert(goalsData, { onConflict: ['user_id'] });

      if (error) throw error;
      
      await fetchGoals();
      setEditingGoals(false);
    } catch (err) {
      console.error('Goals save error:', err);
      setError('Failed to save nutrition goals');
    }
  };

  // Add or update daily entry
  const handleEntrySubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Check if at least one nutrition value is provided
    const hasAnyNutrition = Object.entries(dailyEntry).some(([key, value]) => 
      key !== 'notes' && value && value.trim() !== ''
    );

    if (!hasAnyNutrition) {
      setError('Please enter at least one nutrition value');
      return;
    }

    try {
      const entryData = {
        user_id: user.id,
        entry_date: formatDate(new Date()),
        calories: dailyEntry.calories ? parseInt(dailyEntry.calories, 10) : null,
        protein: dailyEntry.protein ? parseInt(dailyEntry.protein, 10) : null,
        carbs: dailyEntry.carbs ? parseInt(dailyEntry.carbs, 10) : null,
        fats: dailyEntry.fats ? parseInt(dailyEntry.fats, 10) : null,
        notes: dailyEntry.notes?.trim() || ''
      };

      if (editingEntry) {
        const { error } = await supabase
          .from('nutrition_entries')
          .update(entryData)
          .eq('id', editingEntry.id);

        if (error) throw error;
        setEditingEntry(null);
      } else {
        // Check if entry for today already exists
        const todayEntry = nutritionEntries.find(entry => 
          entry.entry_date === formatDate(new Date())
        );

        if (todayEntry) {
          // Update existing entry
          const { error } = await supabase
            .from('nutrition_entries')
            .update(entryData)
            .eq('id', todayEntry.id);

          if (error) throw error;
        } else {
          // Create new entry
          const { error } = await supabase
            .from('nutrition_entries')
            .insert([entryData]);

          if (error) throw error;
        }
      }

      // Reset form and refresh data
      setDailyEntry({
        calories: '',
        protein: '',
        carbs: '',
        fats: '',
        notes: ''
      });
      
      await fetchEntries();
    } catch (err) {
      console.error('Entry save error:', err);
      setError('Failed to save nutrition entry');
    }
  };

  // Edit entry
  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setDailyEntry({
      calories: entry.calories || '',
      protein: entry.protein || '',
      carbs: entry.carbs || '',
      fats: entry.fats || '',
      notes: entry.notes || ''
    });
  };

  // Delete entry
  const handleDeleteEntry = async (entry) => {
    if (!window.confirm('Delete this nutrition entry?')) return;

    try {
      const { error } = await supabase
        .from('nutrition_entries')
        .delete()
        .eq('id', entry.id);

      if (error) throw error;
      await fetchEntries();
    } catch (err) {
      console.error('Delete error:', err);
      setError('Failed to delete entry');
    }
  };

  // Get today's entry
  const getTodayEntry = () => {
    return nutritionEntries.find(entry => entry.entry_date === formatDate(new Date()));
  };

  // Prepare chart data
  const getChartData = () => {
    return nutritionEntries
      .slice()
      .reverse()
      .map(entry => ({
        date: entry.entry_date,
        calories: entry.calories || 0,
        protein: entry.protein || 0,
        carbs: entry.carbs || 0,
        fats: entry.fats || 0,
        displayDate: formatDisplayDate(new Date(entry.entry_date + 'T00:00:00'))
      }));
  };

  // Calculate progress percentage
  const getProgressPercentage = (actual, goal) => {
    if (!goal || !actual) return 0;
    return Math.min((actual / goal) * 100, 100);
  };

  const todayEntry = getTodayEntry();
  const hasGoals = goals.daily_calories || goals.daily_protein || goals.daily_carbs || goals.daily_fats;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 text-center">
          <div className="w-12 h-12 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white">Loading your nutrition data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6">
        <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
          <img src={DietTrackerIcon} alt="Diet" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3" />
          Nutrition Tracker
        </h2>
        <p className="text-purple-300">
          Track your daily calories and macronutrients to reach your fitness goals
        </p>
      </div>

      {error && (
        <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3">
          <p className="text-red-200 text-sm">{error}</p>
        </div>
      )}

      {/* Nutrition Goals */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-white flex items-center">
            <img src={BullseyeIcon} alt="Goal" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3" />
            Daily Goals
          </h3>
          {hasGoals && !editingGoals && (
            <button
              onClick={() => setEditingGoals(true)}
              className="bg-purple-500/80 hover:bg-purple-500 text-white px-4 py-2 rounded-lg transition-all duration-200 text-sm"
            >
              Edit Goals
            </button>
          )}
        </div>

        {editingGoals || !hasGoals ? (
          <form onSubmit={handleGoalsSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-purple-200 mb-2">Daily Calories</label>
                <input
                  type="number"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  value={goals.daily_calories}
                  onChange={(e) => setGoals({...goals, daily_calories: e.target.value})}
                  placeholder="2000"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-purple-200 mb-2">Daily Protein (g)</label>
                <input
                  type="number"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  value={goals.daily_protein}
                  onChange={(e) => setGoals({...goals, daily_protein: e.target.value})}
                  placeholder="150"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-purple-200 mb-2">Daily Carbs (g)</label>
                <input
                  type="number"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  value={goals.daily_carbs}
                  onChange={(e) => setGoals({...goals, daily_carbs: e.target.value})}
                  placeholder="200"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-purple-200 mb-2">Daily Fats (g)</label>
                <input
                  type="number"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  value={goals.daily_fats}
                  onChange={(e) => setGoals({...goals, daily_fats: e.target.value})}
                  placeholder="70"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-200"
              >
                {hasGoals ? 'Update Goals' : 'Set Goals'}
              </button>

              {hasGoals && (
                <button
                  type="button"
                  onClick={() => setEditingGoals(false)}
                  className="bg-gray-500/80 hover:bg-gray-500 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {goals.daily_calories && (
              <div className="bg-blue-500/20 border border-blue-500/50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-white">{goals.daily_calories}</div>
                <div className="text-blue-200 text-sm">Calories</div>
              </div>
            )}
            {goals.daily_protein && (
              <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-white">{goals.daily_protein}g</div>
                <div className="text-green-200 text-sm">Protein</div>
              </div>
            )}
            {goals.daily_carbs && (
              <div className="bg-orange-500/20 border border-orange-500/50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-white">{goals.daily_carbs}g</div>
                <div className="text-orange-200 text-sm">Carbs</div>
              </div>
            )}
            {goals.daily_fats && (
              <div className="bg-purple-500/20 border border-purple-500/50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-white">{goals.daily_fats}g</div>
                <div className="text-purple-200 text-sm">Fats</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Today's Progress */}
      {hasGoals && todayEntry && (
        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6">
          <h3 className="text-xl font-bold text-white mb-6 flex items-center">
            <img src={WorkoutHistoryIcon} alt="History" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3" />
            Today's Progress
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {goals.daily_calories && (
              <div className="text-center">
                <div className="text-2xl font-bold text-white mb-2">
                  {todayEntry.calories || 0} / {goals.daily_calories}
                </div>
                <div className="text-blue-200 text-sm mb-2">Calories</div>
                <div className="w-full bg-white/10 rounded-full h-2">
                  <div 
                    className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${getProgressPercentage(todayEntry.calories, goals.daily_calories)}%` }}
                  ></div>
                </div>
              </div>
            )}

            {goals.daily_protein && (
              <div className="text-center">
                <div className="text-2xl font-bold text-white mb-2">
                  {todayEntry.protein || 0}g / {goals.daily_protein}g
                </div>
                <div className="text-green-200 text-sm mb-2">Protein</div>
                <div className="w-full bg-white/10 rounded-full h-2">
                  <div 
                    className="bg-green-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${getProgressPercentage(todayEntry.protein, goals.daily_protein)}%` }}
                  ></div>
                </div>
              </div>
            )}

            {goals.daily_carbs && (
              <div className="text-center">
                <div className="text-2xl font-bold text-white mb-2">
                  {todayEntry.carbs || 0}g / {goals.daily_carbs}g
                </div>
                <div className="text-orange-200 text-sm mb-2">Carbs</div>
                <div className="w-full bg-white/10 rounded-full h-2">
                  <div 
                    className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${getProgressPercentage(todayEntry.carbs, goals.daily_carbs)}%` }}
                  ></div>
                </div>
              </div>
            )}

            {goals.daily_fats && (
              <div className="text-center">
                <div className="text-2xl font-bold text-white mb-2">
                  {todayEntry.fats || 0}g / {goals.daily_fats}g
                </div>
                <div className="text-purple-200 text-sm mb-2">Fats</div>
                <div className="w-full bg-white/10 rounded-full h-2">
                  <div 
                    className="bg-purple-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${getProgressPercentage(todayEntry.fats, goals.daily_fats)}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Daily Entry Form */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6">
        <h3 className="text-xl font-bold text-white mb-6 flex items-center">
          <img src={BlogDefaultIcon} alt="Blog" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3" />
          {editingEntry ? 'Edit Entry' : 'Log Today\'s Nutrition'}
        </h3>

        <form onSubmit={handleEntrySubmit} className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">Calories</label>
              <input
                type="number"
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                value={dailyEntry.calories}
                onChange={(e) => setDailyEntry({...dailyEntry, calories: e.target.value})}
                placeholder="2000"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">Protein (g)</label>
              <input
                type="number"
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                value={dailyEntry.protein}
                onChange={(e) => setDailyEntry({...dailyEntry, protein: e.target.value})}
                placeholder="150"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">Carbs (g)</label>
              <input
                type="number"
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                value={dailyEntry.carbs}
                onChange={(e) => setDailyEntry({...dailyEntry, carbs: e.target.value})}
                placeholder="200"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">Fats (g)</label>
              <input
                type="number"
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                value={dailyEntry.fats}
                onChange={(e) => setDailyEntry({...dailyEntry, fats: e.target.value})}
                placeholder="70"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-purple-200 mb-2">Notes</label>
            <input
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              value={dailyEntry.notes}
              onChange={(e) => setDailyEntry({...dailyEntry, notes: e.target.value})}
              placeholder="Optional notes about your nutrition today..."
            />
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-200"
            >
              {editingEntry ? 'Update Entry' : 'Log Nutrition'}
            </button>

            {editingEntry && (
              <button
                type="button"
                onClick={() => {
                  setEditingEntry(null);
                  setDailyEntry({
                    calories: '',
                    protein: '',
                    carbs: '',
                    fats: '',
                    notes: ''
                  });
                }}
                className="bg-gray-500/80 hover:bg-gray-500 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Progress Chart */}
      {nutritionEntries.length > 0 && (
        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6">
          <h3 className="text-xl font-bold text-white mb-6 flex items-center">
            <img src={LineGraphIcon} alt="Graph" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3" />
            Nutrition Trends (Last 30 Days)
          </h3>
          
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={getChartData()} margin={{ top: 20, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#666" />
              <XAxis dataKey="displayDate" stroke="#ccc" />
              <YAxis stroke="#ccc" />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="calories" stroke="#3B82F6" name="Calories" />
              <Line type="monotone" dataKey="protein" stroke="#10B981" name="Protein (g)" />
              <Line type="monotone" dataKey="carbs" stroke="#F59E0B" name="Carbs (g)" />
              <Line type="monotone" dataKey="fats" stroke="#8B5CF6" name="Fats (g)" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Recent Entries */}
      {nutritionEntries.length > 0 && (
        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6">
          <h3 className="text-xl font-bold text-white mb-6 flex items-center">
            <img src={BlogDefaultIcon} alt="Blog" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3" />
            Recent Entries
          </h3>

          <div className="space-y-4">
            {nutritionEntries.slice(0, 10).map((entry) => (
              <div key={entry.id} className="bg-white/10 border border-white/10 rounded-lg p-4">
                <div className="flex justify-between items-start mb-2">
                  <div className="font-semibold text-white">
                    {formatDisplayDate(new Date(entry.entry_date + 'T00:00:00'))}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditEntry(entry)}
                      className="text-blue-300 hover:text-blue-200 text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteEntry(entry)}
                      className="text-red-300 hover:text-red-200 text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm text-purple-200">
                  {entry.calories && <div>Calories: {entry.calories}</div>}
                  {entry.protein && <div>Protein: {entry.protein}g</div>}
                  {entry.carbs && <div>Carbs: {entry.carbs}g</div>}
                  {entry.fats && <div>Fats: {entry.fats}g</div>}
                </div>
                
                {entry.notes && (
                  <div className="mt-2 text-sm text-purple-300 italic">
                    Note: {entry.notes}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Getting Started */}
      {nutritionEntries.length === 0 && (
        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-8 text-center">
          <img src={BullseyeIcon} alt="Goal" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3" />
          <h3 className="text-xl font-bold text-white mb-4">Start Tracking Your Nutrition</h3>
          <p className="text-purple-300 mb-6 max-w-2xl mx-auto">
            Set your daily nutrition goals and start logging your intake to monitor your progress toward your fitness goals.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto text-sm">
            <div className="bg-white/5 rounded-lg p-4">
              <div className="text-blue-400 font-medium">Set Goals</div>
              <div className="text-purple-300">Define your daily targets</div>
            </div>
            <div className="bg-white/5 rounded-lg p-4">
              <div className="text-green-400 font-medium">Track Daily</div>
              <div className="text-purple-300">Log your nutrition intake</div>
            </div>
            <div className="bg-white/5 rounded-lg p-4">
              <div className="text-orange-400 font-medium">Monitor Progress</div>
              <div className="text-purple-300">See your trends over time</div>
            </div>
            <div className="bg-white/5 rounded-lg p-4">
              <div className="text-purple-400 font-medium">Reach Goals</div>
              <div className="text-purple-300">Achieve your fitness targets</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}